// Mock portfolio data for Ajay Sunil
// NOTE: This is frontend-only mock data. No backend/API wiring yet.

export const heroData = {
  name: "Ajay Sunil",
  title: "Technical Consultant · AI Engineer",
  headline:
    "Designing intelligent search and AI solutions that actually ship and scale.",
  subtitle:
    "I build practical AI systems, RAG-powered chatbots, and search platforms that turn scattered enterprise data into fast, reliable answers.",
  location: "Bengaluru, India",
  ctaPrimary: "View Projects",
  ctaSecondary: "Get in Touch",
};

export const aboutData = {
  intro:
    "I'm a Technical Consultant and AI Engineer focused on bringing real-world, production-ready AI to enterprises. I enjoy turning fuzzy business problems into clear, reliable systems that people actually want to use.",
  story:
    "At MOURI Tech, I work across the stack—from data ingestion and search pipelines to chat interfaces—to help teams ship AI features faster and with more confidence. I love designing solutions where retrieval, relevance, and user experience come together.",
  values: [
    "Ship value, not just models—production, monitoring, and feedback loops matter.",
    "Design systems that are explainable, observable, and safe for real users.",
    "Collaborate closely with stakeholders to align AI capabilities with business goals.",
  ],
  uniqueness:
    "I sit at the intersection of AI, search, and product thinking: I can design the architecture, build the connectors and APIs, and also craft the UI that makes everything feel simple and approachable.",
};

export const skillsData = {
  categories: [
    {
      name: "AI & NLP",
      description: "Designing retrieval-augmented pipelines and conversational systems.",
      skills: ["OpenAI", "Azure OpenAI", "RAG Pipelines", "NLP"],
    },
    {
      name: "Search Technologies",
      description: "Building search platforms that surface relevant content across messy data.",
      skills: ["Elasticsearch", "Azure AI Search"],
    },
    {
      name: "Backend & Cloud",
      description: "APIs and cloud-native services for scalable, resilient AI backends.",
      skills: [
        "Python",
        "FastAPI",
        "Azure Functions",
        "Azure Bot Service",
        "Azure Blob Storage",
      ],
    },
    {
      name: "Frontend",
      description: "Crafting clean, responsive interfaces for AI and search experiences.",
      skills: ["ReactJS", "Angular", "HTML", "CSS", "Bootstrap"],
    },
    {
      name: "Tools & Workflow",
      description: "Modern tooling for collaboration, version control, and productivity.",
      skills: ["Git", "GitHub", "GitLab", "VS Code", "Cursor"],
    },
  ],
};

export const projectFilters = [
  { id: "all", label: "All" },
  { id: "search", label: "Search Platforms" },
  { id: "chatbots", label: "Chatbots & RAG" },
  { id: "frontend", label: "Frontend UI" },
];

export const projectsData = [
  {
    id: "auraf2",
    title: "AuraF2 – Search and Data Indexing Platform",
    category: "search",
    role: "Backend & Search Engineer",
    summary:
      "Built indexing connectors, chatbot integrations, and RAG-powered search to make enterprise content instantly discoverable.",
    details:
      "Designed and implemented connectors to ingest data from enterprise systems into Elasticsearch, enabling NLP-driven search and RAG-style retrieval for downstream chatbots.",
    outcomes: [
      "Simplified search implementation for multiple internal teams via a reusable connector framework.",
      "Improved search precision with NLP-based relevance tuning and structured entity extraction.",
    ],
    techStack: ["FastAPI", "Elasticsearch", "Python"],
  },
  {
    id: "teams-genai-bot",
    title: "Generative AI Chatbot for Microsoft Teams",
    category: "chatbots",
    role: "AI Engineer",
    summary:
      "Delivered a Teams chatbot that lets employees query organizational knowledge using RAG, with feedback loops for continuous improvement.",
    details:
      "Implemented a retrieval-augmented generation pipeline over internal resources, integrated with Azure Bot Service and Azure Functions to deliver responses directly in Teams.",
    outcomes: [
      "Reduced support load by answering repetitive employee queries inside Teams.",
      "Captured interaction logs and feedback in Blob Storage for monitoring and tuning.",
    ],
    techStack: ["OpenAI", "Azure Bot Service", "Azure Functions", "Python"],
  },
  {
    id: "skillsens-ui",
    title: "SkillSens – Dynamic Skill-Weight UI",
    category: "frontend",
    role: "Frontend Lead",
    summary:
      "Led UI development for a responsive dashboard to fine-tune search relevance via dynamic skill weights.",
    details:
      "Designed and implemented React components that allow users to visually adjust relevance weights and instantly see the impact on search results.",
    outcomes: [
      "Improved transparency of search behavior for business stakeholders.",
      "Enabled non-technical users to experiment with search tuning without touching code.",
    ],
    techStack: ["ReactJS"],
  },
  {
    id: "strada-genai",
    title: "Strada – Enterprise Generative AI Chatbot",
    category: "chatbots",
    role: "Full-Stack AI Engineer",
    summary:
      "Designed and implemented an enterprise-grade chatbot unifying content from SharePoint, Salesforce, Smartsheets, and Jira.",
    details:
      "Built a unified retrieval layer using Azure AI Search as a vector database, orchestrating data across multiple systems to deliver real-time, context-aware answers.",
    outcomes: [
      "Streamlined access to distributed enterprise data sources for internal and client teams.",
      "Improved operational efficiency by automating common client interactions.",
    ],
    techStack: [
      "FastAPI",
      "Azure AI Search",
      "Azure Functions",
      "Azure OpenAI",
      "Blob Storage",
      "Cosmos DB",
      "ReactJS",
    ],
  },
];

export const experienceData = {
  items: [
    {
      company: "MOURI Tech",
      role: "Technical Consultant",
      period: "Jan 2023 – Present",
      location: "Bengaluru, India",
      bullets: [
        "Developed an Elasticsearch wrapper with reusable connectors for data indexing, chatbot integration, and advanced search.",
        "Integrated enterprise CMS data into Elasticsearch, reducing manual curation and boosting internal content discoverability.",
        "Extended the search ecosystem with OCR, entity extraction, and video-to-text pipelines to unlock previously unsearchable content.",
        "Implemented RAG pipelines to deliver context-aware chatbot responses and reduce irrelevant outputs.",
        "Built and deployed a Microsoft Teams chatbot leveraging Azure Bot Service, Azure Functions, and OpenAI.",
        "Designed and led frontend development for a React-based dashboard with dynamic skill-weight adjustment.",
      ],
    },
  ],
};

export const educationData = {
  items: [
    {
      institution: "Lovely Professional University",
      degree: "B.Tech – Computer Science and Engineering",
      period: "Graduated Sep 2020",
      details: [
        "Focused on software engineering, algorithms, and distributed systems.",
        "Laid the foundation for work in backend development, AI, and cloud-native architectures.",
      ],
    },
  ],
};

export const contactData = {
  email: "ajaysunil84@gmail.com",
  phone: "+91 9779276644",
  location: "Bengaluru, KA, India",
  linkedin: "https://www.linkedin.com/in/ajay-sunil",
  note:
    "Whether you’re exploring AI search, RAG chatbots, or modernizing internal knowledge access, I’d be happy to discuss how we can work together.",
};
